import { Component, OnDestroy, OnInit, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { PROCTORED_RULES_NAME_DESCRIPTION } from '@edtech-ui/core/constants/recuritment-constants/proctoring-constants/ruleSets';
import { CreateProctoringRuleRequest, CreateProctoringRulesetPayload } from '@edtech-ui/core/models/proctoring-dashboard-models/create-proctoring-ruleset';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { GetProctoringConfiguration, GetProctoringConfigurationsPagedRequest } from '@edtech-ui/core/models/proctoring-dashboard-models/get-proctroing-config';
import { ToastrService } from 'ngx-toastr';
import { ProctoringService } from '@edtech-ui/core/services/proctoring-dashboard/proctoring.service';
import { ProctoringConfigValidationUtils } from '@edtech-ui/core/utils/proctoring-dashboard/proctoring-config';
import { RULE_TYPE_MAP } from '@edtech-ui/constants/proctoring-services.enum';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
	selector: 'app-add-rules-proctoring',
	standalone: true,
	imports: [FormsModule, CommonModule, ReactiveFormsModule, MatTooltipModule],
	templateUrl: './add-rules-proctoring.component.html',
	styleUrl: './add-rules-proctoring.component.scss'
})
export class AddRulesProctoringComponent implements OnInit, OnDestroy {

	rulesetName = '';
	purpose = '';
	configuration = '';
	isDefault = false;
	dropdownOpen = false;
	selectedRules: any[] = [];
	addedRules: CreateProctoringRuleRequest[] = [];
	availableRules = PROCTORED_RULES_NAME_DESCRIPTION;
	rulesetForm: FormGroup;
	searchText: string = '';
	pageSize = 10;
	currentPage = 1;
	totalPages = 0
	isLoading = false;
	subscription = new Subscription();
	configData: GetProctoringConfiguration[] = [];
	total = 0;
	sortField = '';
	sortOrder: 'asc' | 'desc' = 'asc';
	companyId = '';
	dropdownConfigOpen = false;
	selectedConfigurationName = '';
	selectedConfigurationId = '';
	selectedConfigurationObj: GetProctoringConfiguration | null = null;
	actualDefaultConfigurationObj: GetProctoringConfiguration | null = null;

	constructor(
		private dialogRef: MatDialogRef<AddRulesProctoringComponent>,
		private toastr: ToastrService,
		private proctoringService: ProctoringService,
		private elementRef: ElementRef
	) {
		this.rulesetForm = new FormGroup({
			name: new FormControl('', [Validators.required, ProctoringConfigValidationUtils.configurationNameValidator()]),
			description: new FormControl('', [Validators.required, ProctoringConfigValidationUtils.proctoringDescriptionValidator()]),
			configurationId: new FormControl('')
		})
	}


	@HostListener('document:click', ['$event'])
	onClickOutside(event: Event) {
		if (this.dropdownOpen && !this.elementRef.nativeElement.querySelector('.rule-select-wrapper')?.contains(event.target as Node)) {
			this.dropdownOpen = false;
		}
		if (this.dropdownConfigOpen && !this.elementRef.nativeElement.querySelector('.config-dropdown')?.contains(event.target as Node)) {
			this.dropdownConfigOpen = false;
		}
	}

	ngOnInit(): void {
		this.companyId = localStorage.getItem('company_id') || '';
		this.getProctoringConfiguration();
	}


	toggleRule(rule: any) {
		const index = this.selectedRules.findIndex(x => x.name === rule.name);

		if (index > -1) {
			this.selectedRules.splice(index, 1);
		}
		else {
			this.selectedRules.push(rule);
		}
	}


	isSelected(rule: any) {
		return this.selectedRules.some(x => x.name === rule.name);
	}

	isRuleAdded(rule: any) {
		return this.addedRules.some(x => x.ruleName === rule.name);
	}


	addSelectedRules() {
		if (this.selectedRules.length === 0) {
			this.toastr.error('Select a rule.');
			return;
		}

		let duplicateFound = false;
		this.selectedRules.forEach(rule => {
			const exists = this.addedRules.find(x => x.ruleName === rule.name);
			if (!exists) {
				this.addedRules.push({
					ruleName: rule.name,
					ruleType: RULE_TYPE_MAP[rule.name],
					description: rule.description,
					severity: 'MEDIUM',
					thresholdCount: 3,
					thresholdWindowSeconds: 30,
					escalationAction: 'WARN',
					captureSnapshot: false,
					isEnabled: true,
					debounceSeconds: 5
				});
			} else {
				duplicateFound = true;
			}
		});

		if (duplicateFound) {
			this.toastr.error('Rule already exists in this ruleset.');
		}

		this.dropdownOpen = false;
		this.selectedRules = [];
	}


	removeRule(index: number) {
		this.addedRules.splice(index, 1);
	}


	close() {
		this.dialogRef.close();
	}


	get isAddedRulesValid(): boolean {
		if (this.addedRules.length === 0) return false;
		for (const rule of this.addedRules) {
			if (rule.debounceSeconds == null || rule.debounceSeconds < 1 || rule.debounceSeconds > 300 || !Number.isInteger(rule.debounceSeconds)) return false;
			if (rule.thresholdWindowSeconds == null || rule.thresholdWindowSeconds < 5 || rule.thresholdWindowSeconds > 600 || rule.thresholdWindowSeconds < rule.debounceSeconds || !Number.isInteger(rule.thresholdWindowSeconds)) return false;
			if (rule.thresholdCount == null || rule.thresholdCount < 1 || rule.thresholdCount > 100 || rule.thresholdCount > rule.thresholdWindowSeconds || !Number.isInteger(rule.thresholdCount)) return false;
			if (!rule.severity || !rule.escalationAction) return false;
		}
		return true;
	}

	toggleDefault() {
		this.isDefault = !this.isDefault;
		if (this.isDefault) {
			this.rulesetForm.get('configurationId')?.setErrors(null);
		}
	}

	saveRuleset() {
		if (!this.isDefault && !this.rulesetForm.value.configurationId) {
			this.rulesetForm.get('configurationId')?.setErrors({ required: true });
			this.rulesetForm.get('configurationId')?.markAsTouched();
		}

		if (this.rulesetForm.invalid) {
			this.rulesetForm.markAllAsTouched();
			return;
		}
		if (this.addedRules.length === 0) {
			this.toastr.error('Select a rule.');
			return;
		}
		if (!this.isAddedRulesValid) {
			this.toastr.error('Please fix validation errors in the rules table before saving.');
			return;
		}
		
		let configId = this.rulesetForm.value.configurationId;
		let defaultConfigurationId = configId;
		
		if (this.isDefault && this.actualDefaultConfigurationObj) {
			configId = this.actualDefaultConfigurationObj.configId;
			defaultConfigurationId = this.actualDefaultConfigurationObj.configId;
		}
		const payload: CreateProctoringRulesetPayload = {
			companyId: this.companyId,
			name: this.rulesetForm.value.name!,
			description: this.rulesetForm.value.description!,
			isEnabled: true,
			rules: this.addedRules,
			configurationId: configId,
			defaultConfigurationId: defaultConfigurationId,
		};
		this.dialogRef.close({ 
			payload, 
			selectedConfig: this.selectedConfigurationObj, 
			actualDefaultConfig: this.actualDefaultConfigurationObj,
			isDefault: this.isDefault 
		});
	}


	getProctoringConfiguration() {
		this.isLoading = true;
		const payload: GetProctoringConfigurationsPagedRequest = {
			page: this.currentPage,
			pageSize: this.pageSize,
			filters: {
				companyId: {
					equals: this.companyId
				}
			},
			sort: [
				{
					field: 'createdAt',
					order: 'DESC'
				}
			],
			all: false
		};
		this.subscription.add(
			this.proctoringService.getProctoringConfiguration(payload).pipe(
				tap(response => {
					if (response.success) {
						this.configData = response.data.data;
						this.actualDefaultConfigurationObj = this.configData.find(c => c.isDefault) || null;
						this.total = response.data.pagination.total;
						this.currentPage = response.data.pagination.page;
						this.pageSize = response.data.pagination.pageSize
						this.totalPages = response.data.pagination.totalPages;
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong.Please try again');
					return of(null);
				})
			).subscribe()
		)
	}

	selectConfiguration(config: GetProctoringConfiguration) {
		this.selectedConfigurationId = config.configId;
		this.selectedConfigurationName = config.name;
		this.selectedConfigurationObj = config;
		this.configuration = config.configId;
		this.rulesetForm.patchValue({ configurationId: config.configId });
		this.rulesetForm.get('configurationId')?.markAsTouched();
		this.dropdownConfigOpen = false;
	}


	nextPage() {
		if (this.currentPage < this.totalPages) {
			this.currentPage++;
			this.getProctoringConfiguration();
		}
	}


	previousPage() {
		if (this.currentPage > 1) {
			this.currentPage--;
			this.getProctoringConfiguration();
		}
	}

	preventNonNumericalInput(event: KeyboardEvent): void {
		const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'];
		if (allowedKeys.includes(event.key)) {
			return;
		}
		if (!/^[0-9]$/.test(event.key)) {
			event.preventDefault();
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}
